import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { dayjs, parsePlannedDate } from '../lib/utils';
import { ChevronRight, GripVertical, Plus, CalendarDays } from 'lucide-react';

const parseDistance = (dist: string): number => {
  if (!dist) return 0;
  const match = dist.match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : 0;
};

const workoutTitle = (workout: any): string =>
  (workout.summary || workout.title || workout.type || 'Workout').toUpperCase();

const workoutDescription = (workout: any): string => workout.details || '';

export const RacePlan = ({
  racePlan,
  completedWorkouts = [],
}: {
  racePlan: any;
  completedWorkouts?: any[];
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [showPreviousWeeks, setShowPreviousWeeks] = useState(false);
  const [draggedWorkout, setDraggedWorkout] = useState<any>(null);
  const [dropDate, setDropDate] = useState<string | null>(null);
  const [undoMove, setUndoMove] = useState<{
    workout: any;
    from: string;
  } | null>(null);
  useEffect(() => {
    if (!undoMove) return;
    const timeout = window.setTimeout(() => setUndoMove(null), 5000);
    return () => window.clearTimeout(timeout);
  }, [undoMove]);
  if (!racePlan) return null;

  const trainingPlan = racePlan.training_plan || [];
  const raceDate = dayjs(racePlan.race_date);
  const today = dayjs();

  // Calculate progress
  const allWorkouts = trainingPlan.flatMap((w: any) => w.workouts);
  const totalDistance = allWorkouts.reduce(
    (sum: number, w: any) => sum + parseDistance(w.distance),
    0,
  );
  const completedDistance = allWorkouts
    .filter((w: any) => w.completed)
    .reduce((sum: number, w: any) => sum + parseDistance(w.distance), 0);
  const totalWorkouts = allWorkouts.length;
  const completedWorkoutsCount = allWorkouts.filter(
    (w: any) => w.completed,
  ).length;
  const progressPercent = totalWorkouts
    ? Math.round((completedWorkoutsCount / totalWorkouts) * 100)
    : 0;

  // Find current week
  const currentWeekNum = trainingPlan.findIndex((week: any) => {
    const weekWorkouts = week.workouts;
    if (!weekWorkouts.length) return false;
    const weekStart = parsePlannedDate(weekWorkouts[0].planned_date);
    const weekEnd = parsePlannedDate(
      weekWorkouts[weekWorkouts.length - 1].planned_date,
    );
    return (
      today.isAfter(weekStart.subtract(1, 'day')) &&
      today.isBefore(weekEnd.add(1, 'day'))
    );
  });

  const isPreviousWeek = (week: any) => {
    const weekWorkouts = week.workouts || [];
    if (!weekWorkouts.length) return false;

    const weekEnd = parsePlannedDate(
      weekWorkouts[weekWorkouts.length - 1].planned_date,
    );
    return weekEnd.isBefore(today, 'day');
  };
  const previousWeekCount = trainingPlan.filter(isPreviousWeek).length;
  const visibleTrainingPlan = showPreviousWeeks
    ? trainingPlan
    : trainingPlan.filter((week: any) => !isPreviousWeek(week));

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getCompletedWorkoutsForDay = (date: any) =>
    completedWorkouts.filter((workout: any) =>
      dayjs(workout.completedAt).isSame(date, 'day'),
    );

  const updateWorkoutDate = async (workout: any, date: string) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/${workout.id}/update`,
      {
        method: 'PUT',
        body: JSON.stringify({ ...workout, planned_date: date }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      },
    );

    if (!response.ok) {
      window.dispatchEvent(
        new CustomEvent('showToast', {
          detail: {
            message: 'Unable to move workout. Please try again.',
            type: 'error',
          },
        }),
      );
      return false;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['racePlan'] }),
      queryClient.invalidateQueries({ queryKey: ['plannedWorkouts'] }),
    ]);
    return true;
  };

  const moveWorkout = async (workout: any, date: string) => {
    const from = parsePlannedDate(workout.planned_date).format('YYYY-MM-DD');
    if (from === date) return;
    if (await updateWorkoutDate(workout, date)) {
      setUndoMove({ workout: { ...workout, planned_date: date }, from });
    }
    setDraggedWorkout(null);
    setDropDate(null);
  };

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 px-1 md:space-y-8 md:px-0">
      {/* Progress Card */}
      <button
        type="button"
        className="plan-hero relative overflow-hidden rounded-2xl bg-brand-midnight p-5 shadow-2xl shadow-brand-midnight/20 transition-shadow hover:shadow-brand-midnight/30 md:p-7"
        onClick={() =>
          navigate(
            racePlan.race?.id
              ? { to: '/races/$id', params: { id: String(racePlan.race.id) } }
              : { to: '/races' },
          )
        }
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-black uppercase tracking-[0.18em] text-white">
              {racePlan.race_name || 'Race Plan'}
            </h2>
            <p className="text-sm text-white/65 mt-0.5">
              {raceDate.format('MMM D, YYYY')} · {racePlan.goal_time}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-brand-volt" />
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-baseline justify-between text-sm mb-2.5">
            <span className="font-semibold text-white">
               {completedWorkoutsCount} / {totalWorkouts} sessions complete
            </span>
            <span className="font-bold text-brand-volt">
              {progressPercent}%
            </span>
          </div>
          <div className="h-2 bg-white/15 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-volt rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="flex gap-4 text-xs text-white/60">
          <span>{completedDistance.toFixed(1)} / {totalDistance.toFixed(1)} mi run</span>
          <span>·</span>
          <span>{raceDate.diff(today, 'days')} days until race</span>
        </div>
      </button>

      {/* Training weeks */}
      <div className="space-y-8">
        {previousWeekCount > 0 ? (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <span>{previousWeekCount} earlier weeks hidden</span>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              onClick={() => setShowPreviousWeeks((current) => !current)}
              className="font-semibold text-brand-cobalt hover:text-brand-midnight"
            >
              {showPreviousWeeks ? 'Hide' : 'Show'}
            </button>
          </div>
        ) : null}
        {visibleTrainingPlan.map((week: any) => {
          const weekIdx = trainingPlan.indexOf(week);
          const weekWorkouts = week.workouts || [];
          if (!weekWorkouts.length) return null;

          const firstWorkoutDate = parsePlannedDate(
            weekWorkouts[0].planned_date,
          );
          const weekStart = firstWorkoutDate.startOf('week');
          const weekEnd = weekStart.add(6, 'day');
          const isCurrentWeek = weekIdx === currentWeekNum;
          const weekTotalDistance = weekWorkouts.reduce(
            (sum: number, w: any) => sum + parseDistance(w.distance),
            0,
          );
          const weekCompletedDistance = weekWorkouts
            .filter((w: any) => w.completed)
            .reduce(
              (sum: number, w: any) => sum + parseDistance(w.distance),
              0,
            );
          const weekProgressPercent = weekTotalDistance
            ? Math.min(
                Math.round((weekCompletedDistance / weekTotalDistance) * 100),
                100,
              )
            : 0;

          return (
            <section
              key={weekIdx}
              aria-labelledby={`week-${week.week}`}
              className="surface-card rounded-2xl p-4 sm:p-5"
            >
              {/* Keep the week context separate from the day cards. */}
              <div className="mb-3 flex flex-col gap-3 px-1 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div
                      id={`week-${week.week}`}
                      className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.14em] text-brand-midnight dark:text-white"
                    >
                      Week {week.week}
                      {isCurrentWeek ? (
                        <span className="rounded-full bg-brand-greenSoft px-2 py-0.5 text-[10px] tracking-wide text-brand-midnight">
                          Current week
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-gray-500 dark:text-slate-300">
                      {weekStart.format('MMM D')} –{' '}
                      {weekEnd.format('MMM D, YYYY')}
                    </div>
                  </div>
                  <ButtonLike
                    onClick={(event: any) => {
                      event.stopPropagation();
                      navigate({
                        to: '/plan/workout/new',
                        search: {
                          week: String(week.week),
                          date: weekStart.format('YYYY-MM-DD'),
                        },
                      });
                    }}
                  />
                </div>

                <div className="w-full sm:max-w-64">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-gray-600 dark:text-slate-300">
                    <span>Weekly mileage progress</span>
                    <span>
                      {weekCompletedDistance.toFixed(1)} /{' '}
                      {weekTotalDistance.toFixed(1)} mi
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-brand-volt transition-all"
                      style={{ width: `${weekProgressPercent}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* One glanceable row on desktop; a comfortable stack on mobile. */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-7 md:gap-3">
                {DAYS.map((dayName, dayIdx) => {
                  const dayDate = weekStart.add(dayIdx, 'day');
                  const dayWorkouts = weekWorkouts.filter((w: any) =>
                    parsePlannedDate(w.planned_date).isSame(dayDate, 'day'),
                  );
                  const isToday = dayDate.isSame(today, 'day');
                  const completedWorkoutsForDay =
                    getCompletedWorkoutsForDay(dayDate);
                  return (
                    <article
                      key={dayIdx}
                      onDragOver={(event) => {
                        if (!draggedWorkout) return;
                        event.preventDefault();
                        setDropDate(dayDate.format('YYYY-MM-DD'));
                      }}
                      onDragLeave={() => setDropDate(null)}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (draggedWorkout) {
                          moveWorkout(
                            draggedWorkout,
                            dayDate.format('YYYY-MM-DD'),
                          );
                        }
                      }}
                      className={`flex min-h-36 flex-col rounded-xl border bg-white p-3 transition-colors md:min-h-52 md:p-3.5 ${
                        dropDate === dayDate.format('YYYY-MM-DD')
                          ? 'border-dashed border-brand-green bg-brand-greenSoft/60 ring-2 ring-brand-green/20'
                          : isToday
                            ? 'border-brand-green ring-2 ring-brand-green/20'
                            : dayWorkouts.length === 0
                              ? 'border-dashed border-gray-200 dark:border-slate-600'
                              : 'border-gray-100 dark:border-slate-700'
                      } dark:bg-brand-midnight`}
                    >
                      <div className="flex shrink-0 items-baseline justify-between">
                        <span
                          className={`text-xs font-bold uppercase tracking-wide ${isToday ? 'text-brand-cobalt' : 'text-gray-500 dark:text-slate-300'}`}
                        >
                          {dayName} {dayDate.format('D')}
                        </span>
                        {dayWorkouts.length === 0 ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate({
                                to: '/plan/workout/new',
                                search: {
                                  week: String(week.week),
                                  date: dayDate.format('YYYY-MM-DD'),
                                },
                              });
                            }}
                            className="text-xs font-bold text-brand-cobalt hover:text-brand-midnight"
                          >
                            + Add workout
                          </button>
                        ) : null}
                      </div>
                      <div className="mt-2 min-h-0 flex-1 overflow-hidden">
                        {dayWorkouts.length > 0 ? (
                          dayWorkouts.map((workout: any) => (
                            <div
                              key={workout.id}
                              draggable
                              onDragStart={() => setDraggedWorkout(workout)}
                              onDragEnd={() => {
                                setDraggedWorkout(null);
                                setDropDate(null);
                              }}
                               className={`flex gap-1 rounded-lg border-2 p-2 shadow-sm active:cursor-grabbing ${
                                workout.completed
                                  ? 'border-brand-green/40 bg-brand-greenSoft dark:border-brand-green dark:bg-green-900/20'
                                  : 'border-brand-cobalt/30 bg-brand-blueSoft dark:border-blue-500/50 dark:bg-slate-800'
                              }`}
                            >
                              <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                               <button
                                type="button"
                                onClick={() =>
                                  navigate({
                                    to: '/plan/workout/$id',
                                    params: { id: workout.id },
                                  })
                                }
                                className="min-w-0 flex-1 overflow-hidden text-left"
                              >
                                <p className="line-clamp-2 text-xs font-bold text-gray-900 dark:text-white">
                                  {workoutTitle(workout)}
                                </p>
                                {workoutDescription(workout) ? (
                                  <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-slate-300">
                                    {workoutDescription(workout)}
                                  </p>
                                ) : null}
                               </button>
                               <label className="sr-only" htmlFor={`move-${workout.id}`}>Move {workoutTitle(workout)} to another day</label>
                               <span className="relative shrink-0 self-start">
                                 <CalendarDays className="h-4 w-4 text-gray-400" aria-hidden="true" />
                                 <input
                                   id={`move-${workout.id}`}
                                   type="date"
                                   value={parsePlannedDate(workout.planned_date).format('YYYY-MM-DD')}
                                   onChange={(event) => moveWorkout(workout, event.target.value)}
                                   className="absolute inset-0 h-4 w-4 cursor-pointer opacity-0"
                                 />
                               </span>
                            </div>
                          ))
                        ) : (
                          <div className="flex h-full items-center justify-center text-xs text-gray-400">
                            {dropDate === dayDate.format('YYYY-MM-DD')
                              ? `Drop here for ${dayName}`
                              : completedWorkoutsForDay.length > 0
                                ? 'Completed workout'
                                : 'Rest & recover'}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      {undoMove ? (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center gap-3 rounded-xl bg-brand-midnight px-4 py-3 text-sm text-white shadow-2xl">
          <span className="flex-1">Workout moved</span>
          <button
            type="button"
            onClick={async () => {
              await updateWorkoutDate(undoMove.workout, undoMove.from);
              setUndoMove(null);
            }}
            className="font-bold text-brand-volt"
          >
            Undo
          </button>
        </div>
      ) : null}
    </div>
  );
};

const ButtonLike = ({ onClick }: { onClick: (event: any) => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-bold text-brand-cobalt hover:bg-brand-offWhite dark:border-slate-500 dark:bg-brand-midnight/50 dark:text-blue-400 dark:hover:bg-slate-700"
  >
    <Plus className="h-3.5 w-3.5" />
    Add
  </button>
);
